/**
 * PowerOutageLayer Component
 *
 * Displays power outages from Ausgrid on the map.
 * Data is fetched directly from Ausgrid's undocumented API.
 *
 * Note: Only Ausgrid (Sydney, Central Coast, Hunter) has a working API.
 * Endeavour Energy (Western Sydney) and Essential Energy (Regional NSW)
 * do not have public APIs available.
 */

import { useEffect, useState, useMemo, useCallback, Fragment } from 'react';
import { Polygon, Popup, useMap, Marker } from 'react-leaflet';
import L from 'leaflet';

/**
 * Create a lightning bolt icon for power outage markers.
 */
function createLightningIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: 'power-outage-marker',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="white">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

/**
 * Calculate the centroid of a polygon.
 */
function getPolygonCenter(coords: Array<{ lat: number; lng: number }>): [number, number] {
  if (coords.length === 0) return [0, 0];
  const sumLat = coords.reduce((sum, c) => sum + c.lat, 0);
  const sumLng = coords.reduce((sum, c) => sum + c.lng, 0);
  return [sumLat / coords.length, sumLng / coords.length];
}

// Use our API proxy to avoid CORS issues
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.hipponics.com';
const POWER_OUTAGES_URL = `${API_BASE_URL}/power/outages`;

// Refresh interval - 5 minutes
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface AusgridOutage {
  Coords: Array<{ lat: number; lng: number }>;
  Area: string;
  Cause: string;
  Customers: number;
  EstRestTime: string;
  StartDateTime: string;
  Status: string;
  WebId: string;
}

interface PowerOutageLayerProps {
  enabled?: boolean;
}

/**
 * Get color based on outage cause.
 */
function getOutageColor(cause: string): string {
  const lowerCause = cause.toLowerCase();
  if (lowerCause.includes('bushfire') || lowerCause.includes('fire')) {
    return '#dc2626'; // Red for fire-related
  }
  if (lowerCause.includes('storm') || lowerCause.includes('weather')) {
    return '#7c3aed'; // Purple for weather
  }
  if (lowerCause.includes('planned') || lowerCause.includes('maintenance')) {
    return '#2563eb'; // Blue for planned
  }
  return '#f97316'; // Orange for other unplanned
}

/**
 * Format date/time string from Ausgrid API.
 * Ausgrid returns dates in "/Date(timestamp)/" format.
 */
function formatDateTime(dateStr: string): string {
  if (!dateStr) return 'Unknown';
  try {
    // Ausgrid returns dates in "/Date(1764990300000)/" format
    const match = dateStr.match(/\/Date\((\d+)\)\//);
    if (match) {
      const timestamp = parseInt(match[1], 10);
      const date = new Date(timestamp);
      return date.toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
    // Fallback for other formats
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

/**
 * Decode Ausgrid status codes.
 */
function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    'UPD': 'Under Investigation',
    'CREW': 'Crew On Site',
    'REST': 'Restoring',
    'ASGN': 'Crew Assigned',
    'PLAN': 'Planned Outage',
  };
  return statusMap[status] || status;
}

/**
 * Get fill opacity based on cause - fire-related more prominent.
 */
function getOutageOpacity(cause: string): number {
  const lowerCause = cause.toLowerCase();
  if (lowerCause.includes('bushfire') || lowerCause.includes('fire')) {
    return 0.4; // More visible for fire-related
  }
  return 0.25;
}

export default function PowerOutageLayer({ enabled = false }: PowerOutageLayerProps) {
  const map = useMap();
  const [outages, setOutages] = useState<AusgridOutage[]>([]);
  // Loading/error are captured for future surfacing (toast or banner). For
  // now they are write-only — the previous in-JSX console.log was removed
  // in the mobile-audit Phase 1; the setters still report state changes
  // for a future failure UI to read via context/devtools.
  const [, setIsLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [viewBounds, setViewBounds] = useState<L.LatLngBounds | null>(null);

  /**
   * Fetch outages from our API proxy (which fetches from Ausgrid).
   */
  const fetchOutages = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(POWER_OUTAGES_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch outages: ${response.status}`);
      }

      const data = await response.json();
      const outageList: AusgridOutage[] = data.outages || [];
      setOutages(outageList);

      console.log(`[PowerOutageLayer] Loaded ${data.count} outages from Ausgrid${data.fire_related_count > 0 ? ` (${data.fire_related_count} fire-related)` : ''}`);
    } catch (err) {
      console.error('[PowerOutageLayer] Error fetching outages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load power outages');
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Fetch outages on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      fetchOutages();
    } else {
      setOutages([]);
    }
  }, [enabled, fetchOutages]);

  // Set up auto-refresh
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(fetchOutages, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchOutages]);

  // Track map bounds for viewport filtering. 150ms debounce so a flick-pan
  // (which emits several moveend events as inertia settles) doesn't refilter
  // hundreds of polygon vertices each time — was P0-7 in the 2D perf audit.
  // Matches the existing pattern in HotspotLayer.
  useEffect(() => {
    if (!map) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const updateBounds = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setViewBounds(map.getBounds());
      }, 150);
    };
    updateBounds();
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);
    return () => {
      if (timer) clearTimeout(timer);
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter outages to viewport with buffer
  const visibleOutages = useMemo(() => {
    if (!outages.length || !viewBounds) return outages;

    // Add buffer for smooth panning
    const latSpan = viewBounds.getNorth() - viewBounds.getSouth();
    const lngSpan = viewBounds.getEast() - viewBounds.getWest();
    const latBuffer = latSpan * 0.2;
    const lngBuffer = lngSpan * 0.2;

    const bufferedBounds = L.latLngBounds(
      [viewBounds.getSouth() - latBuffer, viewBounds.getWest() - lngBuffer],
      [viewBounds.getNorth() + latBuffer, viewBounds.getEast() + lngBuffer]
    );

    return outages.filter(outage => {
      // Check if any point of the polygon is in view
      if (!outage.Coords || outage.Coords.length === 0) return false;
      return outage.Coords.some(coord =>
        bufferedBounds.contains([coord.lat, coord.lng])
      );
    });
  }, [outages, viewBounds]);

  if (!enabled) return null;

  return (
    <>
      {visibleOutages.map((outage) => {
        if (!outage.Coords || outage.Coords.length < 3) return null;

        const positions: [number, number][] = outage.Coords.map(c => [c.lat, c.lng]);
        const color = getOutageColor(outage.Cause);
        const opacity = getOutageOpacity(outage.Cause);
        const center = getPolygonCenter(outage.Coords);
        const icon = createLightningIcon(color);

        return (
          <Fragment key={outage.WebId}>
            <Polygon
              positions={positions}
              pathOptions={{
                color: color,
                weight: 2,
                opacity: 0.8,
                fillColor: color,
                fillOpacity: opacity,
              }}
            />
            <Marker position={center} icon={icon} zIndexOffset={-1000}>
              <Popup className="power-outage-popup">
              <div className="power-outage-popup-content">
                <div className="power-outage-popup-header">
                  <span className="power-outage-popup-icon">⚡</span>
                  <span className="power-outage-popup-title">Power Outage</span>
                </div>

                <div className="power-outage-popup-details">
                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Area:</span>
                    <span className="power-outage-popup-value">{outage.Area || 'Unknown'}</span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Cause:</span>
                    <span className="power-outage-popup-value" style={{ color }}>
                      {outage.Cause || 'Under Investigation'}
                    </span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Customers:</span>
                    <span className="power-outage-popup-value">
                      {outage.Customers?.toLocaleString() || 'Unknown'}
                    </span>
                  </div>

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Started:</span>
                    <span className="power-outage-popup-value">
                      {formatDateTime(outage.StartDateTime)}
                    </span>
                  </div>

                  {outage.EstRestTime && (
                    <div className="power-outage-popup-row">
                      <span className="power-outage-popup-label">Est. Restore:</span>
                      <span className="power-outage-popup-value">
                        {formatDateTime(outage.EstRestTime)}
                      </span>
                    </div>
                  )}

                  <div className="power-outage-popup-row">
                    <span className="power-outage-popup-label">Status:</span>
                    <span className="power-outage-popup-value">
                      {formatStatus(outage.Status) || 'Active'}
                    </span>
                  </div>
                </div>

                <div className="power-outage-popup-footer">
                  <span className="power-outage-popup-source">Source: Ausgrid</span>
                  <a
                    href={`https://www.ausgrid.com.au/In-your-community/Outages-and-faults/Outage-map?outageid=${outage.WebId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="power-outage-popup-link"
                  >
                    View Details
                  </a>
                </div>
              </div>
            </Popup>
            </Marker>
          </Fragment>
        );
      })}

    </>
  );
}
